<template>
  <div
    :id="id"
    class="element group"
    :class="className"
    :style="basicStyles"
    @dblclick="dblClick"
  >
    <transition
      :enter-active-class="'animated ' + animationIn"
      :leave-active-class="'animated ' + animationOut"
      :duration="{ leave: leaveDuration }"
      mode=""
      @after-enter="afterEnter"
      @before-leave="beforeLeave"
    >
      <div
        v-show="toggle"
        class="group-wrap"
        :class="{ 'data-list': element?.type === 'data-list' }"
        :style="[
          animationStyles,
          textBackgroundStyles,
          borderStyles,
          boxShadowStyles,
          blurStyles,
        ]"
      >
        <div
          v-if="element.backgroundImage"
          class="background-image"
          :data-test-image-optimized="backgroundMetaOptimizeApplied"
          :style="[
            backgroundStyles,
          ]"
        />
        <slot name="beforeChildElements"></slot>
        <slot
          :select="selectChild"
          :group="id"
          :parent="id"
          :get-element-value-by-prop="getElementValueByProp"
        >
          <element-renderer
            v-for="elementId in childElementsReversed"
            v-show="!getElementById(elementId)?.layerHiddenInGraphics"
            :id="elementId"
            :key="elementId"
            :group="elementId"
            :type="getElementValueByProp(elementId, 'type')"
            :parent="id"
            class="group-item"
            :toggle="toggle"
            :current-post="currentPost"
            :mute-preview="mutePreview"
            @select="selectChild"
          />
        </slot>
      </div>
    </transition>
  </div>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import ElementsMixins from '../../mixins/elements-mixins';
import MoveMixins from '../../mixins/move-mixins';
import ResizeMixins from '../../mixins/resize-mixins';

export default {
  name: 'GroupElement',

  components: {
    ElementRenderer: defineAsyncComponent(() => import('./ElementRenderer')),
  },
  mixins: [ElementsMixins, MoveMixins, ResizeMixins],

  props: {
    // size is used in move-mixins.js for doing data-list stuff
    size: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    // currentPost is required for social data, but not required for other elements
    currentPost: {},
    mutePreview: {},
  },
  data() {
    return {
      altKey: false,
      lastGroupPressAt: 0,
      lastGroupPressTarget: null,
    };
  },
  computed: {
    className() {
      const {
        cssClasses, animationInStyle, animationOutStyle, locked,
      } = this.element;

      const { editing, isSelected } = this;

      return [
        'draggable',
        cssClasses,
        animationInStyle,
        animationOutStyle,
        {
          selected: isSelected,
          locked,
          editing,
          // Repeated rows render outside the element's own bounds, so keep
          // overflow visible when slide animations clip to the element box.
          'data-list': this.element.type === 'data-list',
        },
      ];
    },

    editing() {
      return this.$store.state.templates.selectedGroups.has(this.id);
    },

    leaveDuration() {
      const elDuration = this.element.animationOutDelay + this.element.animationOutDuration;
      const childrenDuration = this.childElements.reduce(
        (total, elementId) => {
          const element = this.getElementById(elementId);
          const duration = element.animation
            ? Number(element.animationOutDuration)
              + Number(element.animationOutDelay)
            : 0;

          return duration >= total ? duration : total;
        },
        0,
      );

      return childrenDuration > elDuration ? childrenDuration : elDuration;
    },

    childElements() {
      return this.getElementGroupMap[this.id] || [];
    },

    childElementsReversed() {
      return [...this.childElements].reverse();
    },
  },

  watch: {
    editing(newVal) {
      // Ignore any changes while user is still holding alt key
      if (this.altKey) {
        return;
      }

      // Sometimes groups are selected/unselected from outside of this component
      if (!newVal) {
        this.removeEditing();
      } else {
        this.setEditing();
      }
    },
    isSelected(v) {
      // make sure that we are tracking group movement only if group is selected
      if (v) this.removeEditing();
    },
  },

  created() {
    // since we create groups by selecting elements first and putting them in
    // we need grouping to properly set up the translation drag configuration
    // for the group and it's children. This avoids the children doubling the movement
    // from both the group and themselves
    this.removeEditing();
  },

  mounted() {
    if (this.editing) {
      this.setEditing();
    } else {
      // Child drag instances are created after this group's created hook.
      this.removeEditing();
    }

    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('keyup', this.onKeyUp);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
  },

  methods: {
    childDraggables() {
      if (!this.$el?.querySelectorAll) return [];
      return [...this.$el.querySelectorAll('.group-item')]
        .filter((el) => el.parentElement?.closest('.group') === this.$el)
        .map((el) => ({ el, drag: Draggable.get(el) }))
        .filter(({ drag }) => drag);
    },
    onGroupPress(event) {
      const target = event.target.closest('.group-item')?.id || this.id;
      const now = Date.now();
      const isDoublePress = target === this.lastGroupPressTarget
        && now - this.lastGroupPressAt < 400;

      this.lastGroupPressAt = isDoublePress ? 0 : now;
      this.lastGroupPressTarget = target;
      if (isDoublePress) this.setEditing();
      return isDoublePress;
    },
    resetGroupPress() {
      this.lastGroupPressAt = 0;
    },
    dblClick() {
      this.setEditing();
    },
    /**
     * Disable group translation and enable editing of children while alt is pressed
     */
    onKeyDown(e) {
      if (e.key === 'Alt' && !this.altKey) {
        this.altKey = true;
        this.setEditing();
      }
    },

    /**
     * Re-enable group translation if alt is released
     */
    onKeyUp(e) {
      if (e.key === 'Alt' && this.altKey) {
        this.altKey = false;
        e.preventDefault(); // Stop the browser menu from showing when releasing alt
        this.removeEditing();
      }
    },

    /**
     * If "editing" a group, disable translation of the group
     *  and enable translation of children
     * This is kinda the secret way to edit elements inside of a group
     */
    setEditing() {
      if (this.translateDrag) {
        this.translateDrag[0].disable();
      }

      this.$store.dispatch('selectGroup', this.id);

      this.childDraggables().forEach(({ el, drag }) => {
        if (!el.classList.contains('group')) {
          drag.enable();
          const child = this.getElementById(el.id);
          TweenMax.set(el, {
            rotation: child?.rotate ?? 0,
            skewX: child?.skewX ?? 0,
            skewY: child?.skewY ?? 0,
            scaleX: child?.scaleX ?? 1,
            scaleY: child?.scaleY ?? 1,
          });
        }
      });
    },

    /**
     * Disable translation of child elements, and re-enable translation of group element
     */
    removeEditing() {
      this.$store.dispatch('unselectGroup', this.id);

      this.childDraggables().forEach(({ drag }) => {
        drag.disable();
      });

      if (this.canTranslateDrag()) this.translateDrag?.[0]?.enable();
    },

    selectChild(event, id) {
      // Child components can still emit a selection while their Draggable is
      // disabled. Until the group has been entered, clicks belong to the group.
      if (!this.editing) {
        this.$store.dispatch('selectElements', [this.id]);
        return;
      }

      if (event.shiftKey) {
        this.$store.dispatch('updateSelectedElements', id);
      } else {
        this.$store.dispatch('selectElements', [id]);
      }
    },
  },
};
</script>
